import React, { useCallback, useEffect, useState } from 'react';

import Swal from 'sweetalert2';

import { MdCheck } from 'react-icons/md';
import { IoMdEye } from 'react-icons/io';
import api from '~/services/api';
import Toast from '~/utils/toast';

import { Container, Status } from './styles';
import { ICheckIn } from '..';
import { KrClose, KrEdit } from '~/components/KoroIcons';
import formatDate from '~/utils/formatDate';

interface IArchiveEvent {
  archive: {
    archive_url: string;
  };
}

interface IEvent {
  name: string;
  type: {
    name: string;
    archive: {
      archive_url: string;
    };
  };
  archivesEvents: IArchiveEvent[];
}

interface ICard {
  checkIn: ICheckIn;
  onEditCheckIn: (event: ICheckIn) => void;
  onDeleteCheckIn: (event: ICheckIn) => void;
  onPublishCheckIn: (event: ICheckIn) => void;
  onListCheckIn: (event: ICheckIn) => void;
}

const Card: React.FC<ICard> = ({
  checkIn,
  onEditCheckIn,
  onDeleteCheckIn,
  onPublishCheckIn,
  onListCheckIn,
}) => {
  const [event, setEvent] = useState({} as IEvent);
  const [presents, setPresents] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get<IEvent>(`events/${checkIn.event_id}`).then(async (response) => {
      setEvent(response.data);
    });
  }, [checkIn]);

  useEffect(() => {
    api
      .get(`tickets/events/${checkIn.event_id}`, {
        params: {
          allStatus: true,
          status: ['Disponível', 'Usado', 'Cortesia'],
        },
      })
      .then((response) => {
        setPresents(response.data.filter((item: any) => item.read).length);
        setTotal(response.data.length);
      });
  }, [checkIn.event_id]);

  const handleListCheckIn = useCallback(
    (checkInData) => {
      onListCheckIn(checkInData);
    },
    [onListCheckIn]
  );

  const handleClickEditOpen = useCallback(
    (checkInData) => {
      onEditCheckIn(checkInData);
    },
    [onEditCheckIn]
  );

  const handleClickPublish = useCallback(
    (checkInData) => {
      Swal.fire({
        icon: 'warning',
        iconColor: '#ff6900',
        title: `Gostaria de habilitar o check-in?`,

        showCancelButton: true,
        cancelButtonText: 'Não',
        confirmButtonText: 'Sim',
        confirmButtonColor: '#ff6900',
        cancelButtonColor: '#B34A00',
        reverseButtons: true,
      }).then(async (e) => {
        if (e.isConfirmed) {
          await api.patch(`events-codes/organizers/enable/${checkInData.id}`);

          onPublishCheckIn(checkInData);

          Toast.fire({
            icon: 'success',
            iconColor: '#ff6900',
            title: 'Check-In habilitado com sucesso!',
          });
        }
      });
    },
    [onPublishCheckIn]
  );

  const handleClickDelete = useCallback(
    (checkInData) => {
      Swal.fire({
        icon: 'warning',
        iconColor: '#ff6900',
        title: `Excluir Check-In!`,
        text: 'Deseja realmente excluir?',
        showCancelButton: true,
        cancelButtonText: 'Não',
        confirmButtonText: 'Sim',
        confirmButtonColor: '#ff6900',
        cancelButtonColor: '#B34A00',
        reverseButtons: true,
      }).then(async (e) => {
        if (e.isConfirmed) {
          await api.delete(`events-codes/${checkInData.id}`);
          onDeleteCheckIn(checkInData);
          Toast.fire({
            icon: 'success',
            iconColor: '#ff6900',
            title: 'Check-In excluído com sucesso!',
          });
        }
      });
    },
    [onDeleteCheckIn]
  );

  return (
    <Container className="d-flex bg-white justify-content-between align-items-center p-3 mt-3">
      {Object.keys(event).length > 0 && (
        <>
          <div className="w-5">
            <img
              src={
                event.archivesEvents.length > 0
                  ? event.archivesEvents[0].archive.archive_url
                  : ''
              }
              alt={event.name}
              className="default"
            />
          </div>
          <div className="w-15">
            <h3 className="mb-1">Nome</h3>
            <p className="mb-0">{event.name}</p>
          </div>
          <div className="w-15">
            <h3 className="mb-1">Código</h3>
            <p className="mb-0">{checkIn.code}</p>
          </div>
          <div className="w-5">
            <div className="d-flex flex-column align-items-center">
              <img
                src={event.type.archive.archive_url}
                alt={event.type.name}
                className="type-icon"
              />
              <p className="mb-0 text-center">{event.type.name}</p>
            </div>
          </div>
          <div>
            <h3 className="mb-1">Data do Check-In</h3>
            <p className="mb-0">
              {checkIn.start_date
                ? `${formatDate(
                    new Date(checkIn.start_date),
                    'dd/MM/yyyy HH:mm'
                  )}`
                : ''}

              <br />
              {checkIn.end_date
                ? `${formatDate(
                    new Date(checkIn.end_date),
                    'dd/MM/yyyy HH:mm'
                  )}`
                : ''}
            </p>
          </div>
          <div className="w-10">
            <h3 className="mb-1">Status</h3>
            <Status
              status={checkIn.status}
              className="text-center rounded-pill mb-0"
            >
              {checkIn.status}
            </Status>
          </div>

          <div>
            <h3 className="mb-1">Presentes</h3>
            <p className="mb-0">{presents}</p>
          </div>
          <div>
            <h3 className="mb-1">Total</h3>
            <p className="mb-0">{total}</p>
          </div>

          <button
            type="button"
            className="ms-2 border-0 position-relative bg-transparent"
            onClick={() => handleListCheckIn(checkIn)}
          >
            <span className="bg-orange rounded-circle">
              <IoMdEye size={16} color="#fff" />
            </span>
          </button>
          <div className={` w-10 text-end`}>
            <button
              type="button"
              disabled={checkIn.status !== 'Cadastrado'}
              className={` ms-2 border-0 position-relative bg-transparent`}
              onClick={() => handleClickPublish(checkIn)}
            >
              <span className="bg-orange rounded-circle">
                <MdCheck size={16} color="#fff" />
              </span>
            </button>
            <button
              type="button"
              disabled={checkIn.status === 'Encerrado'}
              className="ms-2 border-0 position-relative bg-transparent"
              onClick={() => {
                handleClickEditOpen(checkIn);
              }}
            >
              <span
                className="bg-orange rounded-circle"
                // data-tooltip="Editar"
              >
                <KrEdit size={16} color="#fff" />
              </span>
            </button>

            <button
              type="button"
              disabled={checkIn.status !== 'Cadastrado'}
              className={` ms-2 border-0 position-relative bg-transparent`}
              onClick={() => {
                handleClickDelete(checkIn);
              }}
            >
              <span
                className="bg-orange rounded-circle"
                // data-tooltip="Apagar"
              >
                <KrClose size={16} color="#fff" />
              </span>
            </button>
          </div>
        </>
      )}
    </Container>
  );
};

export default Card;
